"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { identity } from "deso-protocol";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import zod from "zod";

import { authWithTwitter } from "@/backend/focus";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { UserContext } from "@/contexts/user-context";
import { DESO_APP_NAME, SocialNetworkMap } from "@/utils/constants";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { useToast } from "../ui/use-toast";

const SocialNetworkBountyAnalyzerFormSchema = zod.object({
  socialNetwork: zod.string({
    error: "Please select a social network",
  }),
});

// For now we only support x.com
const supportedNetworks = Object.entries(SocialNetworkMap)
  .filter(([, value]) => value.name === "x.com")
  .map(([key, value]) => ({ ...value, type: key }));

export const AnalyzeFollowers = () => {
  const { user, loading: loadingUser } = useContext(UserContext);
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const onSocialNetworkBountyAnalyzerFormSubmit = async () => {
    if (!user) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "To analyze your followers, you need to be logged in.",
      });
      return;
    }

    setSubmitting(true);
    const jwt = await identity.jwt();
    const publicKey = user?.publicKey ?? "";

    // TODO: temporarily disabling twitter auth
    const response = await authWithTwitter({ jwt, publicKey });
    if (response === null) {
      setSubmitting(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: "An error happened when authenticating with Twitter",
      });
    }
  };

  const socialNetworkBountyAnalyzerForm = useForm<
    zod.infer<typeof SocialNetworkBountyAnalyzerFormSchema>
  >({
    resolver: zodResolver(SocialNetworkBountyAnalyzerFormSchema),
    defaultValues: {
      socialNetwork: supportedNetworks[0].name,
    },
    mode: "onChange",
  });

  return (
    <Form {...socialNetworkBountyAnalyzerForm}>
      <form
        onSubmit={socialNetworkBountyAnalyzerForm.handleSubmit(
          onSocialNetworkBountyAnalyzerFormSubmit,
        )}
        className="w-full rounded-2xl text-left"
      >
        <div className="mb-4 space-y-2">
          <FormField
            control={socialNetworkBountyAnalyzerForm.control}
            name="socialNetwork"
            render={({ field }) => (
              <FormItem className="flex w-full flex-wrap items-center">
                <FormLabel htmlFor="social-network">Social Network</FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl className="min-h-[38px] border border-border">
                    <SelectTrigger id="social-network">
                      <SelectValue placeholder="Select a social network" />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent>
                    {supportedNetworks.map((e) => (
                      <SelectItem
                        value={e.name}
                        key={e.url}
                        className="cursor-pointer"
                      >
                        {e.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <section className="mt-4 text-center">
          {loadingUser ? (
            <Skeleton className="mb-8 h-10" />
          ) : user ? (
            <div
              className="
                flex flex-col gap-4 text-xs
                sm:items-center
              "
            >
              <p>Connect your external account and analyze your network.</p>
              <Button
                // disabled={!user || submitting}
                disabled={true}
                onClick={onSocialNetworkBountyAnalyzerFormSubmit}
              >
                {/*{submitting ? "Analyzing..." : "Analyze Your Followers"}*/}
                {submitting ? "Analyzing..." : "Temporarily disabled"}
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 text-xs">
              <p>
                To analyze your network you should be{" "}
                <span className="font-medium">logged in</span> with your{" "}
                {DESO_APP_NAME} Account.
              </p>
              <Button onClick={() => identity.login()}>
                Log In or Sign Up
              </Button>
            </div>
          )}

          <div className="mt-2 hidden text-xs text-muted">
            This requires a $0.10 fee to prevent spam
          </div>
        </section>
      </form>
    </Form>
  );
};
